/**
 * KM MOTOS — Controller de Selector de Motos & Compatibilidad
 * Asset: assets/kmm-moto-selector.js
 *
 * Persiste la moto seleccionada en localStorage ('km_selected_moto')
 * Emite evento 'km:moto-changed' para reactividad global.
 * Evalúa compatibilidad garantizada en cards y PDP.
 */
(function (window, document) {
  'use strict';

  var STORAGE_KEY = 'km_selected_moto';
  var LEGACY_KEY = 'km.moto';

  function norm(str) {
    if (!str) return '';
    return str
      .toString()
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]/g, '');
  }

  function escapeHtml(str) {
    if (!str) return '';
    return String(str).replace(/[&<>"']/g, function (m) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[m];
    });
  }

  function normalizeMotoRecord(data, allowNumberCoercion) {
    if (!data || typeof data !== 'object') return null;
    var rawBrand = data.brand !== undefined && data.brand !== null ? data.brand : data.marca;
    var rawModel = data.model !== undefined && data.model !== null ? data.model : data.modelo;
    var validBrandType = typeof rawBrand === 'string' || (allowNumberCoercion && typeof rawBrand === 'number');
    var validModelType = typeof rawModel === 'string' || (allowNumberCoercion && typeof rawModel === 'number');
    if (!validBrandType || !validModelType) return null;

    var brandStr = String(rawBrand).trim();
    var modelStr = String(rawModel).trim();
    if (!brandStr || !modelStr) return null;

    var rawYear = data.year !== undefined && data.year !== null ? data.year : (data.anos !== undefined && data.anos !== null ? data.anos : '');
    var yearStr = (rawYear !== 0 && rawYear !== '0' && rawYear !== '') ? String(rawYear).trim() : '';
    var parsedYear = yearStr ? (parseInt(yearStr, 10) || yearStr) : null;
    var label = data.label || [brandStr, modelStr, yearStr].filter(Boolean).join(' ');
    var displayName = data.displayName || (brandStr + ' ' + modelStr + (yearStr ? ' (' + yearStr + ')' : ''));
    var handle = data.handle || norm(brandStr + '-' + modelStr);
    return {
      id: data.id || handle,
      brand: brandStr,
      model: modelStr,
      year: parsedYear,
      displayName: displayName,
      handle: handle,
      collectionUrl: data.collectionUrl || '',
      marca: brandStr,
      modelo: modelStr,
      cilindraje: data.cilindraje || '',
      anos: yearStr,
      label: label,
      skus: Array.isArray(data.skus) ? data.skus : [],
      updatedAt: data.updatedAt || Date.now()
    };
  }

  var KmMotoSelector = {
    get: function () {
      try {
        if (typeof localStorage === 'undefined') return null;
        var raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
          return normalizeMotoRecord(JSON.parse(raw), false);
        }
        var legacyRaw = localStorage.getItem(LEGACY_KEY);
        if (legacyRaw) {
          var legacyParsed = normalizeMotoRecord(JSON.parse(legacyRaw), false);
          if (legacyParsed) {
            try {
              localStorage.setItem(STORAGE_KEY, JSON.stringify(legacyParsed));
            } catch (e) {}
            return legacyParsed;
          }
        }
        return null;
      } catch (e) {
        console.warn('[KM Moto] Error reading localStorage:', e);
        return null;
      }
    },

    set: function (moto) {
      var payload = normalizeMotoRecord(moto, true);
      if (!payload) {
        return this.clear();
      }
      payload.updatedAt = Date.now();

      try {
        if (typeof localStorage !== 'undefined') {
          var serialized = JSON.stringify(payload);
          localStorage.setItem(STORAGE_KEY, serialized);
          localStorage.setItem(LEGACY_KEY, serialized);
        }
      } catch (e) {
        console.warn('[KM Moto] Error writing to localStorage:', e);
      }

      this.broadcast(payload);
      this.updateBadges(payload);
      this.updateStickyPills(payload);
      return payload;
    },

    clear: function () {
      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(LEGACY_KEY);
        }
      } catch (e) {
        console.warn('[KM Moto] Error clearing localStorage:', e);
      }
      this.broadcast(null);
      this.updateBadges(null);
      this.updateStickyPills(null);
    },

    broadcast: function (moto) {
      try {
        var event = new CustomEvent('km:moto-changed', {
          detail: moto,
          bubbles: true
        });
        window.dispatchEvent(event);
      } catch (e) {
        console.error('[KM Moto] Error dispatching custom event:', e);
      }
    },

    isCompatible: function (moto, compatList) {
      if (!moto || !compatList || !Array.isArray(compatList) || compatList.length === 0) {
        return false;
      }
      var targetBrand = norm(moto.brand);
      var targetModel = norm(moto.model);
      if (!targetBrand || !targetModel || targetModel.length < 2) {
        return false;
      }

      for (var i = 0; i < compatList.length; i++) {
        var item = compatList[i];
        if (!item) continue;
        var rawB = '';
        if (item.brand && typeof item.brand === 'object') {
          rawB = (item.brand.value !== undefined && item.brand.value !== null) ? item.brand.value : (item.brand.brand_text || item.brand.name || item.brand_text || '');
        } else {
          rawB = item.brand || item.brand_text || '';
        }
        var b = norm(rawB);

        var rawM = '';
        if (item.model && typeof item.model === 'object') {
          rawM = (item.model.value !== undefined && item.model.value !== null) ? item.model.value : (item.model.modelo_title || item.model.name || item.modelo_title || item.name || '');
        } else {
          rawM = item.model || item.modelo_title || item.name || '';
        }
        var m = norm(rawM);

        if (!m || m.length < 2) continue;

        var brandMatch = (!b) ? false : (b === targetBrand);
        if (!brandMatch) continue;

        // 1. Direct exact normalized match
        if (m === targetModel) {
          return true;
        }

        // 2. Strict tokenized match for multi-model definitions (e.g. "FT125 / FT150")
        var rawModel = String(rawM);
        if (rawModel.indexOf('/') !== -1 || rawModel.indexOf(',') !== -1 || rawModel.indexOf(';') !== -1 || rawModel.indexOf('|') !== -1) {
          var tokens = rawModel.split(/[\/,;|]/);
          for (var t = 0; t < tokens.length; t++) {
            var tok = norm(tokens[t]);
            if (tok && tok.length >= 2 && tok === targetModel) {
              return true;
            }
          }
        }
      }
      return false;
    },

    updateStickyPills: function (moto) {
      var currentMoto = moto !== undefined ? moto : this.get();
      var pills = document.querySelectorAll('[data-kmm-sticky-pill]');
      for (var i = 0; i < pills.length; i++) {
        var pill = pills[i];
        if (currentMoto) {
          pill.style.display = 'flex';
          var textEl = pill.querySelector('[data-kmm-pill-text]');
          if (textEl) {
            textEl.textContent = currentMoto.displayName;
          }
        } else {
          pill.style.display = 'none';
        }
      }
    },

    updateBadges: function (moto) {
      var currentMoto = moto !== undefined ? moto : this.get();

      // 1. Update Product Card badges
      var cardContainers = document.querySelectorAll('[data-kmm-card-compat]');
      for (var i = 0; i < cardContainers.length; i++) {
        var card = cardContainers[i];
        var rawCompat = card.getAttribute('data-compat-list');
        var compatList = [];
        try {
          compatList = rawCompat ? JSON.parse(rawCompat) : [];
        } catch (e) {
          compatList = [];
        }

        var statusTarget = card.querySelector('[data-kmm-status-target]') || card;

        if (!currentMoto) {
          // Neutral State: Show prompt or default compatibility list
          card.classList.remove('kmm-compat--match', 'kmm-compat--mismatch');
          card.classList.add('kmm-compat--neutral');
          var fallbackHtml = card.getAttribute('data-default-html');
          if (fallbackHtml) {
            statusTarget.innerHTML = fallbackHtml;
          } else {
            statusTarget.innerHTML =
              '<span class="kmm-compat__prompt">' +
              '<span class="kmm-compat__icon">🏍️</span> ' +
              '¿Es para tu moto? <a href="#hero-garaje" class="kmm-prompt-link">Selecciona tu modelo aquí</a>' +
              '</span>';
          }
        } else {
          var matched = this.isCompatible(currentMoto, compatList);
          var cardDisplayName = currentMoto.displayName ? currentMoto.displayName : currentMoto.model;
          var safeCardName = escapeHtml(cardDisplayName);
          if (matched) {
            card.classList.remove('kmm-compat--mismatch', 'kmm-compat--neutral');
            card.classList.add('kmm-compat--match');
            statusTarget.innerHTML =
              '<div class="kmm-compat-badge kmm-compat-badge--green">' +
              '<svg class="kmm-icon-check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>' +
              '<span>Compatible con tu <strong>' + safeCardName + '</strong> (Garantizado)</span>' +
              '</div>';
          } else {
            card.classList.remove('kmm-compat--match', 'kmm-compat--neutral');
            card.classList.add('kmm-compat--mismatch');
            statusTarget.innerHTML =
              '<div class="kmm-compat-badge kmm-compat-badge--amber">' +
              '<svg class="kmm-icon-warn" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>' +
              '<span>No compatible con tu <strong>' + safeCardName + '</strong></span>' +
              '</div>';
          }
        }
      }

      // 2. Update Product Detail Page (PDP) Badges
      var pdpContainers = document.querySelectorAll('[data-kmm-pdp-compat]');
      for (var j = 0; j < pdpContainers.length; j++) {
        var pdp = pdpContainers[j];
        var pdpCompatRaw = pdp.getAttribute('data-compat-list');
        var pdpCompatList = [];
        try {
          pdpCompatList = pdpCompatRaw ? JSON.parse(pdpCompatRaw) : [];
        } catch (e) {
          pdpCompatList = [];
        }

        var pdpContent = pdp.querySelector('[data-kmm-pdp-content]') || pdp;

        if (!currentMoto) {
          pdp.className = 'kmm-pdp-compat kmm-pdp-compat--neutral';
          pdpContent.innerHTML =
            '<div class="kmm-pdp-card">' +
            '<div class="kmm-pdp-icon">🏍️</div>' +
            '<div class="kmm-pdp-text">' +
            '<div class="kmm-pdp-title">¿Es para tu moto? Selecciona tu modelo aquí</div>' +
            '<div class="kmm-pdp-sub">Verificá compatibilidad garantizada antes de comprar.</div>' +
            '</div>' +
            '<button type="button" class="kmm-btn-select-moto" onclick="window.KmMotoSelector.openModalOrScroll()">' +
            'Elegir mi moto' +
            '</button>' +
            '</div>';
        } else {
          var isPdpMatch = this.isCompatible(currentMoto, pdpCompatList);
          var pdpDisplayName = currentMoto.displayName ? currentMoto.displayName : currentMoto.model;
          var safePdpName = escapeHtml(pdpDisplayName);
          if (isPdpMatch) {
            pdp.className = 'kmm-pdp-compat kmm-pdp-compat--match';
            pdpContent.innerHTML =
              '<div class="kmm-pdp-card kmm-pdp-card--verified">' +
              '<div class="kmm-pdp-icon kmm-pdp-icon--green">' +
              '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>' +
              '</div>' +
              '<div class="kmm-pdp-text">' +
              '<div class="kmm-pdp-title">✓ Compatible con tu ' + safePdpName + ' (Garantizado)</div>' +
              '<div class="kmm-pdp-sub">Ajuste 100% verificado. Si no le queda a tu moto, cambio o devolución sin costo en nuestras 88 tiendas.</div>' +
              '</div>' +
              '<button type="button" class="kmm-btn-change-moto" onclick="window.KmMotoSelector.clear()">' +
              'Cambiar moto' +
              '</button>' +
              '</div>';
          } else {
            pdp.className = 'kmm-pdp-compat kmm-pdp-compat--mismatch';
            pdpContent.innerHTML =
              '<div class="kmm-pdp-card kmm-pdp-card--warning">' +
              '<div class="kmm-pdp-icon kmm-pdp-icon--amber">' +
              '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>' +
              '</div>' +
              '<div class="kmm-pdp-text">' +
              '<div class="kmm-pdp-title">⚠ No compatible con tu ' + safePdpName + '</div>' +
              '<div class="kmm-pdp-sub">Esta pieza no está listada para tu modelo. Evitá compras erróneas.</div>' +
              '</div>' +
              '<a href="/search?q=' + encodeURIComponent(currentMoto.brand + ' ' + currentMoto.model) + '" class="kmm-btn-find-compat">' +
              'Ver para mi moto &rarr;' +
              '</a>' +
              '</div>';
          }
        }
      }
    },

    openModalOrScroll: function () {
      var hero = document.getElementById('kmm-hero-selector') || document.getElementById('hero-garaje') || document.getElementById('selector-hero');
      if (hero) {
        hero.scrollIntoView({ behavior: 'smooth', block: 'center' });
        var brandSel = hero.querySelector('#kmm-hero-brand, #kmm-garage-brand, select');
        if (brandSel) brandSel.focus();
        return;
      }
      var selectorSection = document.querySelector('.section-selector-auto, .kmm-hero-section, .kmm-home-hero');
      if (selectorSection) {
        selectorSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.location.href = '/#hero-garaje';
      }
    },

    initHero: function (container) {
      if (!container || container.dataset.kmmInitialized) return;
      container.dataset.kmmInitialized = 'true';

      var brandSelect = container.querySelector('#kmm-hero-brand');
      var modelSelect = container.querySelector('#kmm-hero-model');
      var yearSelect = container.querySelector('#kmm-hero-year');
      var quickInput = container.querySelector('#kmm-hero-quick-search');
      var submitBtn = container.querySelector('#kmm-hero-submit');
      var dataScript = container.querySelector('#kmm-motos-data');

      var motosCatalog = [];
      if (dataScript) {
        try {
          motosCatalog = JSON.parse(dataScript.textContent || '[]');
        } catch (e) {
          console.warn('[KM Moto] Error parsing catalog JSON:', e);
        }
      }

      // Populate Brands
      if (brandSelect && brandSelect.options.length <= 1) {
        var brandsMap = {};
        for (var i = 0; i < motosCatalog.length; i++) {
          var b = (motosCatalog[i].brand || '').trim();
          if (b && !brandsMap[b.toLowerCase()]) {
            brandsMap[b.toLowerCase()] = b;
          }
        }
        var sortedBrands = Object.keys(brandsMap).sort();
        for (var j = 0; j < sortedBrands.length; j++) {
          var opt = document.createElement('option');
          opt.value = sortedBrands[j];
          opt.textContent = brandsMap[sortedBrands[j]];
          brandSelect.appendChild(opt);
        }
      }

      function updateModels() {
        if (!modelSelect) return;
        var selectedBrand = brandSelect ? brandSelect.value.toLowerCase() : '';
        modelSelect.innerHTML = '<option value="">2. Selecciona Modelo</option>';
        if (yearSelect) {
          yearSelect.innerHTML = '<option value="">3. Año</option>';
          yearSelect.disabled = true;
        }
        if (submitBtn) submitBtn.disabled = true;

        if (!selectedBrand) {
          modelSelect.disabled = true;
          return;
        }

        var modelsForBrand = [];
        for (var k = 0; k < motosCatalog.length; k++) {
          var item = motosCatalog[k];
          if ((item.brand || '').toLowerCase() === selectedBrand) {
            modelsForBrand.push(item);
          }
        }

        modelsForBrand.sort(function (a, b) {
          return (a.model || '').localeCompare(b.model || '');
        });

        for (var l = 0; l < modelsForBrand.length; l++) {
          var mOpt = document.createElement('option');
          mOpt.value = modelsForBrand[l].model;
          mOpt.textContent = modelsForBrand[l].model;
          if (modelsForBrand[l].collectionUrl) {
            mOpt.setAttribute('data-collection-url', modelsForBrand[l].collectionUrl);
          }
          modelSelect.appendChild(mOpt);
        }
        modelSelect.disabled = false;
      }

      function updateYears() {
        if (!yearSelect) return;
        var selectedModel = modelSelect ? modelSelect.value : '';
        yearSelect.innerHTML = '<option value="">3. Año</option>';

        if (!selectedModel) {
          yearSelect.disabled = true;
          if (submitBtn) submitBtn.disabled = true;
          return;
        }

        // Years from 2026 down to 2012 (covers most active moto fleets in Honduras)
        var currentYear = 2026;
        for (var yr = currentYear; yr >= 2012; yr--) {
          var yOpt = document.createElement('option');
          yOpt.value = yr;
          yOpt.textContent = yr;
          yearSelect.appendChild(yOpt);
        }
        yearSelect.disabled = false;
        if (submitBtn) submitBtn.disabled = false;
      }

      if (brandSelect) {
        brandSelect.addEventListener('change', updateModels);
      }
      if (modelSelect) {
        modelSelect.addEventListener('change', updateYears);
      }
      if (yearSelect) {
        yearSelect.addEventListener('change', function () {
          if (submitBtn) submitBtn.disabled = false;
        });
      }

      // Quick Search input for direct model typing
      if (quickInput) {
        quickInput.addEventListener('input', function () {
          var val = norm(this.value);
          if (val.length < 2) return;
          for (var idx = 0; idx < motosCatalog.length; idx++) {
            var item = motosCatalog[idx];
            var combo = norm((item.brand || '') + ' ' + (item.model || ''));
            if (combo.indexOf(val) !== -1) {
              if (brandSelect) brandSelect.value = (item.brand || '').toLowerCase();
              updateModels();
              if (modelSelect) modelSelect.value = item.model;
              updateYears();
              break;
            }
          }
        });
      }

      if (submitBtn) {
        submitBtn.addEventListener('click', function (e) {
          e.preventDefault();
          var bVal = brandSelect ? brandSelect.options[brandSelect.selectedIndex].text : '';
          var mVal = modelSelect ? modelSelect.value : '';
          var yVal = yearSelect ? yearSelect.value : '';

          if (!bVal || !mVal) {
            var errEl = container.querySelector('.kmm-hero-inline-error');
            if (!errEl) {
              errEl = document.createElement('p');
              errEl.className = 'kmm-hero-inline-error';
              errEl.setAttribute('role', 'alert');
              errEl.setAttribute('aria-live', 'polite');
              errEl.style.cssText = 'margin:0.25rem 0;padding:0.55rem 0.85rem;border-radius:8px;background:#fef2f2;border:1px solid #fecaca;color:#b91c1c;font-size:0.88rem;font-weight:600;';
              submitBtn.parentNode.insertBefore(errEl, submitBtn);
            }
            errEl.textContent = 'Por favor selecciona Marca y Modelo.';
            errEl.style.display = 'block';
            if (!bVal && brandSelect) brandSelect.focus();
            else if (!mVal && modelSelect) modelSelect.focus();
            return;
          }
          var existingErr = container.querySelector('.kmm-hero-inline-error');
          if (existingErr) existingErr.style.display = 'none';

          var selectedOpt = modelSelect ? modelSelect.options[modelSelect.selectedIndex] : null;
          var colUrl = selectedOpt ? selectedOpt.getAttribute('data-collection-url') : '';

          var savedMoto = KmMotoSelector.set({
            brand: bVal,
            model: mVal,
            year: yVal,
            collectionUrl: colUrl
          });

          // Navigate to collection or search query
          if (colUrl) {
            window.location.href = colUrl;
          } else {
            window.location.href = '/search?q=' + encodeURIComponent(bVal + ' ' + mVal);
          }
        });
      }

      // Restore active bike if already saved
      var current = KmMotoSelector.get();
      if (current && brandSelect && modelSelect) {
        brandSelect.value = current.brand.toLowerCase();
        updateModels();
        modelSelect.value = current.model;
        updateYears();
        if (current.year && yearSelect) {
          yearSelect.value = current.year;
        }
        if (submitBtn) submitBtn.disabled = false;
      }
    }
  };

  // Bind global reactivity
  window.KmMotoSelector = KmMotoSelector;
  window.kmGetSelectedMoto = function () { return KmMotoSelector.get(); };
  window.kmSetSelectedMoto = function (m) { return KmMotoSelector.set(m); };
  window.kmClearSelectedMoto = function () { return KmMotoSelector.clear(); };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = KmMotoSelector;
  }

  if (typeof window.addEventListener === 'function') {
    window.addEventListener('km:moto-changed', function (e) {
      KmMotoSelector.updateBadges(e.detail);
      KmMotoSelector.updateStickyPills(e.detail);
    });

    window.addEventListener('storage', function (e) {
      if (e.key === STORAGE_KEY || e.key === LEGACY_KEY) {
        var newMoto = null;
        if (e.newValue) {
          try {
            newMoto = normalizeMotoRecord(JSON.parse(e.newValue));
          } catch (err) {}
        }
        if (!newMoto) {
          newMoto = KmMotoSelector.get();
        }
        KmMotoSelector.broadcast(newMoto);
      }
    });
  }

  function boot() {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return;
    }
    KmMotoSelector.updateBadges();
    KmMotoSelector.updateStickyPills();
    if (typeof document.querySelectorAll === 'function') {
      var heroes = document.querySelectorAll('[data-kmm-hero]');
      for (var i = 0; i < heroes.length; i++) {
        KmMotoSelector.initHero(heroes[i]);
      }
    }
    if (typeof document.addEventListener === 'function' && !document.documentElement.dataset.kmmPromptBound) {
      document.documentElement.dataset.kmmPromptBound = 'true';
      document.addEventListener('click', function (e) {
        var link = e.target && e.target.closest ? e.target.closest('.kmm-prompt-link, a[href="#kmm-hero-selector"], a[href="#selector-hero"], a[href="#hero-garaje"]') : null;
        if (link) {
          e.preventDefault();
          KmMotoSelector.openModalOrScroll();
        }
      });
    }
  }

  if (typeof document.addEventListener === 'function') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }
  }
  if (typeof window.addEventListener === 'function') {
    window.addEventListener('pageshow', boot);
  }

})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : this), typeof document !== 'undefined' ? document : { readyState: 'complete', addEventListener: function(){}, querySelectorAll: function(){ return []; } });
